import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsIncome } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS, ANALYTICS_DATE_STORAGE_KEY } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getFilterTags, getFilterValues } from '../utils/helpers'
import { getCurrentDate } from '@/helpers/dates'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTagsBunchStore } from '@/store/tags/bunch'
import { useRepoLocalStorage } from '@/hooks/repoLocalStorage'

import useAnalyticsStore from '../index'

const TABLE_NAME = 'analytics_income'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  date: 'VARCHAR',
  account_id: 'VARCHAR',
  asset_id: 'VARCHAR',
  category_account_id: 'VARCHAR',
  tags: 'VARCHAR[]',
}

export default defineStore('analytics-income', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const analyticsStore = useAnalyticsStore()

  const list = ref<Record<string, AnalyticsIncome[]>>()

  const loading = ref(false)
  const deprecated = ref<boolean>()

  const storageDate = useRepoLocalStorage<string | undefined>(
    ANALYTICS_DATE_STORAGE_KEY,
    undefined,
  )

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getList = computed(() => {
    if (analyticsStore.getError) return {}
    return list.value
  })

  const getDatesRange = computed(() => {
    return list.value && Object.keys(list.value)
  })

  const getIncomes = computed(() => {
    if (!storageDate.value) return []
    return getList.value?.[storageDate.value] || []
  })

  // SETTERS

  const setList = async (data: {
    income_view: Record<string, AnalyticsIncome[]>
  }) => {
    if (deprecated.value === false) return
    if (!data.income_view) return
    list.value = data.income_view
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.values(data.income_view).map(income =>
        duckdbInstance.pushData(TABLE_NAME, income, TABLE_SCHEMA),
      ),
    )
  }

  // ACTIONS

  const clear = () => {
    list.value = undefined
    loading.value = false
    deprecated.value = undefined
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,

    getList,
    getIncomes,
    getDatesRange,

    setList,

    clear,
  }
})

export const useAnalyticsIncomeFilters = () => {

  const analyticsStore = useAnalyticsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues<AnalyticsIncome>(TABLE_NAME, analyticsDate, assetsBunchStore)
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_ID],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE)
      },
      {
        name: AnalyticsField.ASSET_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_ID],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE)
      },
      {
        name: AnalyticsField.CATEGORY_ACCOUNT_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY_ACCOUNT_ID],
        getter: () => getValues(AnalyticsField.CATEGORY_ACCOUNT_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.CATEGORY_ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY_ACCOUNT_TYPE],
        getter: () => getValues(AnalyticsField.CATEGORY_ACCOUNT_ID, ASSET_FIELD.TYPE)
      },
      {
        name: AnalyticsField.CATEGORY,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY],
        getter: () => getTags(AnalyticsField.CATEGORY)
      },
      {
        name: AnalyticsField.SUBCATEGORY,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.SUBCATEGORY],
        getter: () => getTags(AnalyticsField.SUBCATEGORY)
      },
    ]
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}


